/**
 * Spend by member (DEC-196, DEC-199). Admins only, the way the Claude app shows an admin
 * each member's usage. A member's private scans are counted and costed, never named.
 *
 * DEC-199: the table opens on the month the chart above it highlights, so the two never
 * disagree, and a pill switches it to all time. Debt trend summaries cost money but are not
 * scans, so they get their own row, and a Total row shows the rows add up to the figure
 * the chart (or the all-time total) shows. No em-dashes (DEC-069).
 */
import { useEffect, useState } from 'react'
import type { UsageMember, UsageResponse } from '../../types/api'

function money(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function memberName(m: UsageMember): string {
  if (m.name) return m.name
  return m.user_id ? 'A member' : 'Not started by a person'
}

function Pill({ on, onClick, children }: { on: boolean; onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={`px-3 py-[6px] rounded-full text-[12px] font-semibold leading-none whitespace-nowrap transition-colors ${
        on ? 'bg-accent text-white' : 'bg-accent/[0.12] text-body hover:bg-accent/[0.2]'
      }`}
    >
      {children}
    </button>
  )
}

export function UsageCard({ data, monthLabel }: { data: UsageResponse; monthLabel: string }) {
  const [range, setRange] = useState<'month' | 'all'>('month')
  // A different month picked on the chart brings the table back to that month.
  useEffect(() => setRange('month'), [data.current_month])

  const month = range === 'month'
  const members = month ? data.by_member_current_month : data.by_member
  const summaries = (month ? data.unattributed_cost_current_month : data.unattributed_cost) ?? 0
  const bucket = data.by_month.find((b) => b.key === data.current_month)
  const total = month ? bucket?.estimated_cost ?? 0 : data.total_estimated_cost
  const scans = month ? bucket?.scans ?? 0 : data.total_scans

  return (
    <div className="bg-panel border border-hairline rounded-md overflow-hidden mb-4" data-testid="usage-card">
      <div className="px-4 py-3 border-b border-fill-subtle flex items-center justify-between gap-3 flex-wrap">
        <span className="font-semibold text-[10px] tracking-[2px] uppercase text-muted">Spend by member</span>
        <div className="flex items-center gap-2" role="group" aria-label="Period">
          <Pill on={month} onClick={() => setRange('month')}>{monthLabel}</Pill>
          <Pill on={!month} onClick={() => setRange('all')}>All time</Pill>
        </div>
      </div>
      <div className="px-4 py-[14px]">
        {!members ? (
          <p className="m-0 text-[12px] text-muted">Spend by member is not available from this server yet.</p>
        ) : members.length === 0 && summaries === 0 ? (
          <p className="m-0 text-[12px] text-muted">
            No spend {month ? `in ${monthLabel}` : 'recorded yet'}.
          </p>
        ) : (
          <table className="w-full text-[13px]" data-testid="usage-by-member">
            <thead>
              <tr className="text-left text-[11px] text-muted">
                <th scope="col" className="font-medium pb-1">Member</th>
                <th scope="col" className="font-medium pb-1 text-right">Scans</th>
                <th scope="col" className="font-medium pb-1 text-right">Of which private</th>
                <th scope="col" className="font-medium pb-1 text-right">Spend</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m, i) => (
                <tr key={m.user_id ?? `none-${m.name}-${i}`} className="border-t border-hairline">
                  <td className="py-2 text-ink">{memberName(m)}</td>
                  <td className="py-2 text-right tabular-nums">{m.scan_count}</td>
                  <td className="py-2 text-right tabular-nums text-muted">{m.private_scan_count}</td>
                  <td className="py-2 text-right tabular-nums">{money(m.spend)}</td>
                </tr>
              ))}
              {summaries > 0 && (
                <tr className="border-t border-hairline" data-testid="usage-summaries-row">
                  <td className="py-2 text-ink">Debt trend summaries</td>
                  <td className="py-2 text-right tabular-nums text-muted">0</td>
                  <td className="py-2 text-right tabular-nums text-muted">0</td>
                  <td className="py-2 text-right tabular-nums">{money(summaries)}</td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="border-t border-hairline-strong" data-testid="usage-total-row">
                <th scope="row" className="py-2 text-left font-semibold text-ink">
                  Total {month ? `in ${monthLabel}` : 'all time'}
                </th>
                <td className="py-2 text-right tabular-nums font-semibold text-ink">{scans}</td>
                <td className="py-2 text-right tabular-nums text-muted">
                  {members.reduce((n, m) => n + m.private_scan_count, 0)}
                </td>
                <td className="py-2 text-right tabular-nums font-semibold text-ink">{money(total)}</td>
              </tr>
            </tfoot>
          </table>
        )}
        <p className="m-0 mt-3 text-[11px] text-muted">Private scans are counted and costed, never named.</p>
      </div>
    </div>
  )
}
